import AddComment20 from '@carbon/icons-react/es/add-comment/20';
import ArrowUpRight20 from '@carbon/icons-react/es/arrow--up-right/20';
import Cursor_120 from '@carbon/icons-react/es/cursor--1/20';
import Edit20 from '@carbon/icons-react/es/edit/20';
import Maximize20 from '@carbon/icons-react/es/maximize/20';
import Minimize20 from '@carbon/icons-react/es/minimize/20';
import RotateClockwise20 from '@carbon/icons-react/es/rotate--clockwise/20';
import Stop20 from '@carbon/icons-react/es/stop/20';
import ViewOff20 from '@carbon/icons-react/es/view--off/20';
import View20 from '@carbon/icons-react/es/view/20';
import ZoomIn20 from '@carbon/icons-react/es/zoom--in/20';
import ZoomOut20 from '@carbon/icons-react/es/zoom--out/20';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { Divider, Dropdown, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { ColorChangeHandler, TwitterPicker } from 'react-color';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import { Discussion } from 'api/document/types/document';
import { ButtonWrapper } from 'components/molecules/views/components';
import { useLocalStorage } from 'hooks/use-local-storage';
import { getRGBFromHex } from 'utils/get-rgb-from-hex';
import {
  ANNOTATION_ARROW_MODE,
  ANNOTATION_COLOR_LS_KEY,
  ANNOTATION_EDIT_MODE,
  ANNOTATION_RECT_MODE,
  ANNOTATION_SQUIGGLY_MODE,
  ANNOTATION_STICKY_MODE,
  COLORS50,
  getAvailableToolFromWebviewerInstance,
  ZOOM_THRESHOLD,
} from './constants';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--coolest-gray-20);
  padding: var(--spacing-02) 0;
  border-bottom: 1px solid var(--gray-20);
`;

const ToolButtonWrapper = styled(ButtonWrapper)<{ active?: boolean }>`
  margin: 0 var(--spacing-02);
  padding: var(--spacing-03);
  background-color: ${(props) =>
    props.active ? 'var(--coolest-gray-40)' : 'transparent'};
  user-select: none;
  &:hover {
    background-color: ${(props) =>
      props.active ? 'var(--coolest-gray-40)' : 'var(--coolest-gray-30)'};
  }
`;

const StyledDivider = styled(Divider).attrs(() => ({ type: 'vertical' }))`
  height: 90%;
`;

const ColorPreview = styled.div<{ color: string }>`
  width: var(--spacing-06);
  height: var(--spacing-06);
  border-radius: 50%;
  background-color: ${prop('color')};
  border: 1px solid var(--border-color);
`;

interface Props {
  instance: WebViewerInstance | null;
  activeTool: string;
  zoom: number;
  showResolvedComments?: boolean;
  discussions?: Discussion[];
  filteredDiscussions?: Discussion[];
  annotationVisiblity?: boolean;
  toggleAnnotationVisiblity?: () => void;
}

export const PreviewToolbar: React.FC<Props> = ({
  instance,
  activeTool,
  zoom,
  discussions,
  filteredDiscussions,
  showResolvedComments,
  annotationVisiblity,
  toggleAnnotationVisiblity,
}) => {
  const [initialColorValue, setColorToLS] = useLocalStorage<string>(
    ANNOTATION_COLOR_LS_KEY,
    '#fa4d56',
  );
  const [color, setColor] = useState<string>(initialColorValue);
  const [visiblity, setVisiblity] = useState(annotationVisiblity ?? true);

  const updateAnnotationVisiblity = (
    annVisiblity: boolean,
    ann: Core.Annotations.Annotation,
  ) => {
    if (instance) {
      const { annotationManager } = instance.Core;

      // eslint-disable-next-line no-param-reassign
      ann.Hidden = annVisiblity;
      annotationManager.updateAnnotation(ann);
    }
  };

  const setTool = useCallback(
    (toolName: string | Core.Tools.ToolNames = ANNOTATION_EDIT_MODE) => {
      const docViewer = instance?.Core.documentViewer;

      if (docViewer && instance) {
        if (
          docViewer.getToolMode().name === ANNOTATION_EDIT_MODE ||
          docViewer.getToolMode().name !== toolName
        ) {
          docViewer?.setToolMode(docViewer?.getTool(toolName));
        } else {
          docViewer?.setToolMode(
            docViewer?.getTool(instance?.Core.Tools.ToolNames.EDIT || ''),
          );
        }
      }
    },
    [instance],
  );

  const onColorChange: ColorChangeHandler = (_color) => {
    setColor(_color.hex);
    instance?.Core.annotationManager.deselectAllAnnotations();
    if (!activeTool || activeTool === ANNOTATION_EDIT_MODE) {
      setTool(instance?.Core.Tools.ToolNames.ARROW2);
    }
  };

  const onViewBtnClick = useCallback(
    () => setTool(ANNOTATION_EDIT_MODE),
    [setTool],
  );

  const onAddComment = useCallback(
    () => setTool(ANNOTATION_STICKY_MODE),
    [setTool],
  );
  const onArrowAnnotationClick = () =>
    setTool(instance?.Core.Tools.ToolNames.ARROW2);

  const onRectangleAnnotationClick = () =>
    setTool(instance?.Core.Tools.ToolNames.RECTANGLE2);

  const onSquigglyClick = () =>
    setTool(instance?.Core.Tools.ToolNames.FREEHAND2);

  const onZoomInClick = () => {
    if (instance) {
      instance.UI.setZoomLevel(instance.UI.getZoomLevel() + 0.5);
    }
  };

  const onZoomOutClick = () => {
    if (instance) {
      instance.UI.setZoomLevel(instance.UI.getZoomLevel() - 0.5);
    }
  };

  const onRotateClick = () => {
    if (instance) {
      instance.Core.documentViewer.rotateClockwise();
      instance.Core.annotationManager.deselectAllAnnotations();
    }
  };

  const onToggleFullScreenClick = () => {
    if (instance) {
      instance.UI.setFitMode(instance.UI.FitMode.FitPage);
    }
  };

  const onToggleVisiblityClick = () => {
    if (instance) {
      const { annotationManager } = instance.Core;
      const annotationsList = annotationManager.getAnnotationsList();
      let resolvedAnnotationList = annotationsList;

      if (filteredDiscussions && discussions) {
        const guidList = showResolvedComments
          ? filteredDiscussions
              .map((e) => e.comments)
              .flatMap((e) => e)
              .map((comment) => comment.commentGuid)
          : discussions
              .map((e) => e.comments)
              .flatMap((e) => e)
              .map((comment) => comment.commentGuid);

        resolvedAnnotationList = annotationsList.filter((ann) =>
          guidList.includes(ann.Id),
        );
      }

      if (!visiblity) {
        resolvedAnnotationList.forEach((ann) =>
          updateAnnotationVisiblity(false, ann),
        );
      } else {
        resolvedAnnotationList.forEach((ann) =>
          updateAnnotationVisiblity(true, ann),
        );
      }
      if (toggleAnnotationVisiblity) {
        toggleAnnotationVisiblity();
      }
      setVisiblity((prev) => !prev);
      instance.Core.annotationManager.deselectAllAnnotations();
    }
  };

  useEffect(() => {
    if (instance && color) {
      const { documentViewer, Annotations } = instance.Core;

      const colorChangeTools = getAvailableToolFromWebviewerInstance(instance);

      colorChangeTools.forEach((tool) => {
        const [r, g, b] = getRGBFromHex(color.replace('#', ''));

        documentViewer.getTool(tool).setStyles(() => ({
          StrokeColor: new Annotations.Color(r, g, b, 1),
        }));
      });
      setColorToLS(color);
    }
  }, [color, instance, setColorToLS]);

  return (
    <Wrapper>
      <Tooltip title="View">
        <ToolButtonWrapper
          active={activeTool === ANNOTATION_EDIT_MODE}
          onClick={onViewBtnClick}
        >
          {/* eslint-disable-next-line react/jsx-pascal-case */}
          <Cursor_120 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Comment">
        <ToolButtonWrapper
          active={activeTool.includes(ANNOTATION_STICKY_MODE)}
          onClick={onAddComment}
        >
          <AddComment20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Arrow">
        <ToolButtonWrapper
          active={activeTool.includes(ANNOTATION_ARROW_MODE)}
          onClick={onArrowAnnotationClick}
        >
          <ArrowUpRight20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Rectangle">
        <ToolButtonWrapper
          active={activeTool.includes(ANNOTATION_RECT_MODE)}
          onClick={onRectangleAnnotationClick}
        >
          <Stop20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Freehand">
        <ToolButtonWrapper
          active={activeTool.includes(ANNOTATION_SQUIGGLY_MODE)}
          onClick={onSquigglyClick}
        >
          <Edit20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Color">
        <Dropdown
          placement="bottomCenter"
          overlay={
            <TwitterPicker
              onChange={onColorChange}
              triangle="hide"
              colors={COLORS50}
              width="170px"
              styles={{
                default: {
                  swatch: { borderRadius: '50%' },
                  input: { display: 'none' },
                  hash: { display: 'none' },
                },
              }}
            />
          }
        >
          <ToolButtonWrapper>
            <ColorPreview color={color} />
          </ToolButtonWrapper>
        </Dropdown>
      </Tooltip>
      <StyledDivider />
      <Tooltip title="Zoom to fit">
        <ToolButtonWrapper onClick={onToggleFullScreenClick}>
          {zoom > ZOOM_THRESHOLD ? <Minimize20 /> : <Maximize20 />}
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Zoom in">
        <ToolButtonWrapper onClick={onZoomInClick}>
          <ZoomIn20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip title="Zoom out">
        <ToolButtonWrapper onClick={onZoomOutClick}>
          <ZoomOut20 />
        </ToolButtonWrapper>
      </Tooltip>
      <StyledDivider />
      <Tooltip title="Rotate Clockwise">
        <ToolButtonWrapper onClick={onRotateClick}>
          <RotateClockwise20 />
        </ToolButtonWrapper>
      </Tooltip>
      <Tooltip
        title={<FormattedMessage id="image_preview.show_hide_annotations" />}
      >
        <ToolButtonWrapper onClick={onToggleVisiblityClick}>
          {visiblity ? <ViewOff20 /> : <View20 />}
        </ToolButtonWrapper>
      </Tooltip>
    </Wrapper>
  );
};
